import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Building2,
  LayoutDashboard,
  Layers,
  Users,
  MapPin,
  Palette,
  Signpost,
} from 'lucide-react';

import { PageShell } from '@/components/primitives/PageShell';
import { PageHeader } from '@/components/primitives/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
// Legacy JS api modules — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as tenantsApi from '@/api/tenants';

import OverviewTab from './hub/OverviewTab';
import ProfileTab from './hub/ProfileTab';
import BrandingTab from './hub/BrandingTab';
import BoundariesTab from './hub/BoundariesTab';
import StreetsTab from './hub/StreetsTab';
import MembersParcelsTab from './hub/MembersParcelsTab';

export interface FPATenant {
  id: number;
  name?: string | null;
  area?: string | null;
  registration_number?: string | null;
  contact_info?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  logo_url?: string | null;
  primary_color?: string | null;
  fdi_share_message?: string | null;
}

type TabId =
  | 'overview'
  | 'profile'
  | 'branding'
  | 'boundaries'
  | 'streets'
  | 'members-parcels';

const tabs: { id: TabId; label: string; icon: typeof Building2 }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'profile', label: 'Profile', icon: Building2 },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'boundaries', label: 'Boundaries', icon: Layers },
  { id: 'streets', label: 'Streets', icon: Signpost },
  { id: 'members-parcels', label: 'Members & parcels', icon: Users },
];

/**
 * FPA Settings Hub — the self-service home for an FPA admin.
 *
 * URL: `/fpa-settings?tab=...`. Super-admins can manage any tenant by
 * passing `?tenant_id=N`; otherwise the hub loads the current user's own
 * tenant. The four tabs are independent components so the heavy ones
 * (boundary editor) don't block first paint of lighter ones (profile).
 */
export default function FPASettingsHub() {
  const { currentUser, isSuperAdmin } = useAuth();
  const { tenantId: switcherTenantId } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tenant resolution for super-admins (priority: URL param > TopBar switcher > own tenant).
  // Regular staff are always pinned to their own tenant regardless.
  const tenantParam = searchParams.get('tenant_id');
  const requestedTenantId = tenantParam ? Number(tenantParam) : null;
  const ownTenantId =
    typeof currentUser?.tenant_id === 'number' ? currentUser.tenant_id : null;
  const effectiveTenantId = isSuperAdmin()
    ? (requestedTenantId ?? switcherTenantId ?? ownTenantId)
    : ownTenantId;

  const [tenant, setTenant] = useState<FPATenant | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const requestedTab = searchParams.get('tab') as TabId | null;
  const tab: TabId =
    requestedTab && tabs.some((t) => t.id === requestedTab)
      ? requestedTab
      : 'overview';

  const setTab = (next: string) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  const refreshTenant = async () => {
    if (effectiveTenantId == null) return;
    try {
      const data = (await tenantsApi.getTenant(effectiveTenantId)) as FPATenant;
      setTenant(data);
      setLoadError(null);
    } catch (err) {
      // Common case: user lands here with no tenant assigned yet — surface
      // a friendly message instead of a console-only error.
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to load FPA details';
      setLoadError(String(message));
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (effectiveTenantId == null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const data = (await tenantsApi.getTenant(effectiveTenantId)) as FPATenant;
        if (cancelled) return;
        setTenant(data);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { detail?: string } }; message?: string })
            ?.response?.data?.detail ||
          (err as { message?: string })?.message ||
          'Failed to load FPA details';
        setLoadError(String(message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveTenantId]);

  if (effectiveTenantId == null) {
    return (
      <PageShell>
        <PageHeader
          title="FPA settings"
          subtitle="Your Fire Protection Association"
        />
        <Alert variant="destructive">
          <AlertDescription>
            Your account isn&apos;t assigned to an FPA yet. Ask a super-admin
            to assign you, or pick a tenant from the global admin area.
          </AlertDescription>
        </Alert>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title={tenant?.name || `FPA ${effectiveTenantId}`}
        subtitle={
          tenant?.area
            ? `${tenant.area} · ${tabs.find((t) => t.id === tab)?.label}`
            : tabs.find((t) => t.id === tab)?.label
        }
      />

      {loadError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex h-auto flex-wrap gap-1">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.id} value={t.id} className="gap-2">
                  <Icon className="h-4 w-4" />
                  {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <OverviewTab
              tenantId={effectiveTenantId}
              tenant={tenant}
              onGoToTab={setTab}
            />
          </TabsContent>
          <TabsContent value="profile" className="mt-0">
            <ProfileTab
              tenantId={effectiveTenantId}
              tenant={tenant}
              onSaved={refreshTenant}
            />
          </TabsContent>
          <TabsContent value="branding" className="mt-0">
            <BrandingTab
              tenantId={effectiveTenantId}
              tenant={tenant}
              onSaved={refreshTenant}
            />
          </TabsContent>
          <TabsContent value="boundaries" className="mt-0">
            <BoundariesTab tenantId={effectiveTenantId} tenant={tenant} />
          </TabsContent>
          <TabsContent value="streets" className="mt-0">
            <StreetsTab tenantId={effectiveTenantId} />
          </TabsContent>
          <TabsContent value="members-parcels" className="mt-0">
            <MembersParcelsTab tenantId={effectiveTenantId} />
          </TabsContent>
        </Tabs>
      )}

      {/* Helper hint for super-admins — most other users won't see this
          tenant_id query param so calling it out reduces support pings. */}
      {isSuperAdmin() && requestedTenantId != null && (
        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" />
          <span>
            Viewing tenant #{effectiveTenantId} as super-admin. Drop the{' '}
            <code>tenant_id</code> query parameter to return to your own.
          </span>
        </div>
      )}
    </PageShell>
  );
}
